/**
 * 작성자 : 홍선영
 * 날짜 : 2024.01.22
 * 기능 : 장비 출입 현황 대시보드 컴포넌트 (구역별 상황판)
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { formatMMDDHHMMSS } from '../../utils/formatDate';
import { PhotoTableListStyle } from '../../assets/styles/PhotoTableList';
import { apiGet } from '../../services/_common';
import IssueGuide from '../IssueGuide';
import SkeletonTable from '../SkeletonTable';
import { ERROR } from '../../_constants';

interface Iprops {
  name: string;
  userInfo: any;
  tatCd: string;
  refetchInterval: number;
}

interface IequipAttend {
  tatGubun: string;
  tadCd: string;
  tadName: string;
  wImg: string;
  wCd: string;
  wName: string;
  sjCd: string;
  sjName: string;
  wPrejobtype: string;
  wPrejobtypeName?: any;
  wJobtype: string;
  wJobtypeName?: any;
  bDate: string;
}

export const EquipAttend = ({ name, userInfo, tatCd, refetchInterval }: Iprops) => {
  const { t } = useTranslation();
  const { hCd, sCd } = userInfo;

  // 장비 출입현황 조회
  const { data, isLoading, isError } = useQuery(['tAreaEquipAttendGet', hCd, sCd, tatCd], () => fetchData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000' && !!tatCd,
    refetchInterval,
  });

  const fetchData = async () => {
    try {
      const req = { hCd, sCd, tatCd };
      const res = await apiGet({ path: '/tarea/board/equipment', req });
      const tareaList = res.data.data?.tareaList || [];
      return tareaList;
    } catch (error) {
      console.error(error);
      throw new Error('error /msg');
    }
  };

  const loading = !data || isLoading;
  if (isError) return <IssueGuide text={ERROR} type='error' />;

  return (
    <PhotoTableListStyle>
      <div className='widget-header safety'>
        <div className='widget-title flex-between'>
          <span>
            {t(name)}
            <span className='badge'>{data?.length ?? 0}</span>
          </span>
        </div>
      </div>
      {loading ? (
        <SkeletonTable rows={14} />
      ) : (
        <div className='widget-body photoTable'>
          <div className='sliderWrapper'>
            <div className='text-container active' style={data.length > 14 ? { animation: `slide ${data.length * 2}s linear infinite` } : undefined}>
              <WorkerList workerList={data} />
            </div>
          </div>
        </div>
      )}
    </PhotoTableListStyle>
  );
};

const WorkerList = ({ workerList }: { workerList: IequipAttend[] }) => {
  const { t } = useTranslation();

  return (
    <div>
      {workerList.map((worker: IequipAttend) => (
        <div key={`${worker.wCd}`} className='rowWrapper flex-basic'>
          <div className='flex-basic'>
            <div className='imgWrapper'>{worker.wImg ? <img src={worker.wImg} alt='' /> : <span className='material-symbols-rounded'>person </span>}</div>
            <div className='flex-col'>
              <div className='flex-basic flex-between'>
                <div className='ellipsis' style={{ paddingRight: '0.5rem' }}>
                  {worker.wName}
                </div>
                <div className='buttonsWrapper flex-basic'>
                  <button type='button' className={worker.tatGubun?.toLocaleLowerCase() === 's' ? 'blue' : 'amber'}>
                    <span>{worker.tatGubun?.toLocaleLowerCase() === 's' ? t('시점') : t('종점')}</span>
                  </button>
                  <button type='button' className='violet'>
                    <span>{worker.tadName}</span>
                  </button>
                </div>
              </div>
              <div className='flex-basic flex-between'>
                <div className='ellipsis' style={{ paddingRight: '0.5rem' }}>
                  {worker.sjName} {worker.wPrejobtypeName}
                </div>
                <div className='buttonsWrapper flex-basic'>
                  <button type='button' className='gray'>
                    <span>{formatMMDDHHMMSS(worker.bDate)}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
